import { Component } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Modal } from 'antd';
import { Trans, withTranslation, WithTranslation } from 'react-i18next';
import {
  parseOnboardingQrPayload,
  ParsedOnboardingQr,
} from '../src/onboardingQr';
import {
  cameraErrorMessage,
  getQrCameraIdOrConfig,
  getQrScannerConfig,
  improveQrCameraFocus,
} from '../src/qrCamera';
import {
  supportsNativeQrScan,
  startNativeCamera,
  stopNativeCamera,
  startNativeDetectLoop,
} from '../src/nativeQrScan';
import './SignupQrScanner.css';

interface Props extends WithTranslation {
  visible: boolean;
  onClose: () => void;
  onScanned: (parsed: ParsedOnboardingQr) => void;
}

interface State {
  error: string | null;
  starting: boolean;
  /** 'native' | 'html5' — controls which preview element is visible */
  mode: 'native' | 'html5' | null;
}

const SCANNER_ELEMENT_ID = 'signup-onboarding-qr-reader';
const NATIVE_VIDEO_ID = 'signup-onboarding-qr-native-video';

class SignupQrScanner extends Component<Props, State> {
  private scanner: Html5Qrcode | null = null;
  private nativeStream: MediaStream | null = null;
  private stopDetect: (() => void) | null = null;
  private isStarting = false;
  private decodeLocked = false;

  constructor(props: Props) {
    super(props);
    this.state = { error: null, starting: false, mode: null };
  }

  componentWillUnmount() {
    void this.stopScanner();
  }

  handleAfterOpenChange = (open: boolean) => {
    if (open) {
      this.setState({ error: null, mode: null });
      // Wait a tick so Ant Modal has mounted preview elements
      setTimeout(() => {
        void this.startScanner();
      }, 100);
    } else {
      void this.stopScanner();
    }
  };

  waitForElement = async (id: string, retries = 20): Promise<boolean> => {
    for (let i = 0; i < retries; i++) {
      if (document.getElementById(id)) {
        return true;
      }
      await new Promise((r) => setTimeout(r, 50));
    }
    return false;
  };

  startScanner = async () => {
    if (
      this.isStarting ||
      this.scanner ||
      this.nativeStream ||
      !this.props.visible
    ) {
      return;
    }

    this.isStarting = true;
    this.decodeLocked = false;
    this.setState({ starting: true, error: null });

    try {
      if (await supportsNativeQrScan()) {
        await this.startNativePath();
      } else {
        await this.startHtml5Path();
      }
      this.setState({ starting: false });
    } catch (err: unknown) {
      this.scanner = null;
      this.setState({
        starting: false,
        error: cameraErrorMessage(err),
      });
    } finally {
      this.isStarting = false;
    }
  };

  startNativePath = async () => {
    // Show <video> before play() — some Android builds skip decode when hidden.
    this.setState({ mode: 'native' });
    await new Promise((r) => setTimeout(r, 50));

    const ready = await this.waitForElement(NATIVE_VIDEO_ID);
    const video = document.getElementById(
      NATIVE_VIDEO_ID,
    ) as HTMLVideoElement | null;
    if (!ready || !video) {
      throw new Error('Native video element missing');
    }

    this.nativeStream = await startNativeCamera(video);
    this.stopDetect = startNativeDetectLoop(video, this.handleDecoded);
  };

  startHtml5Path = async () => {
    this.setState({ mode: 'html5' });
    const ready = await this.waitForElement(SCANNER_ELEMENT_ID);
    if (!ready) {
      throw new Error('Scanner element missing');
    }

    const scanner = new Html5Qrcode(SCANNER_ELEMENT_ID);
    this.scanner = scanner;
    await scanner.start(
      getQrCameraIdOrConfig(),
      getQrScannerConfig(),
      this.handleDecoded,
      () => {
        // ignore frame scan misses
      },
    );
    void improveQrCameraFocus(scanner);
  };

  handleDecoded = (decodedText: string) => {
    if (this.decodeLocked) return;
    this.decodeLocked = true;

    const parsed = parseOnboardingQrPayload(decodedText);
    if (!parsed) {
      this.decodeLocked = false;
      this.setState({
        error: this.props.t(
          'c3d4e5f6-a7b8-9012-cdef-123456789012',
          'This QR code is not a Ripple Maker Account QR. Try again.',
        ),
      });
      return;
    }

    void this.stopScanner().then(() => {
      this.props.onScanned(parsed);
    });
  };

  stopScanner = async () => {
    if (this.stopDetect) {
      this.stopDetect();
      this.stopDetect = null;
    }
    if (this.nativeStream) {
      stopNativeCamera(this.nativeStream);
      this.nativeStream = null;
    }
    const video = document.getElementById(
      NATIVE_VIDEO_ID,
    ) as HTMLVideoElement | null;
    if (video) {
      video.srcObject = null;
    }

    const scanner = this.scanner;
    this.scanner = null;
    if (!scanner) {
      return;
    }
    try {
      if (scanner.isScanning) {
        await scanner.stop();
      }
      scanner.clear();
    } catch {
      // ignore stop errors
    }
  };

  render() {
    const { starting, mode, error } = this.state;

    return (
      <Modal
        open={this.props.visible}
        onCancel={this.props.onClose}
        afterOpenChange={this.handleAfterOpenChange}
        footer={null}
        centered
        destroyOnClose
        closable
        className="signup-qr-modal"
        title={
          <Trans i18nKey="7d204aa1-5ac1-4986-89bc-2833b6da3f5f">
            Scan QR code
          </Trans>
        }
      >
        <p className="signup-qr-modal__hint">
          <Trans i18nKey="7cfb2761-17ba-4604-848d-67902c8dc48b">
            Scan the QR code to continue activating your Ripple Maker in the
            mobile app.
          </Trans>
        </p>
        <div
          className={`signup-qr-modal__camera-wrap${
            mode === 'native' ? ' is-native' : ''
          }`}
        >
          <video
            id={NATIVE_VIDEO_ID}
            className="signup-qr-modal__native-video"
            playsInline
            muted
            autoPlay
          />
          <div id={SCANNER_ELEMENT_ID} className="signup-qr-modal__camera" />
          {starting && (
            <div className="signup-qr-modal__overlay">Starting camera…</div>
          )}
        </div>
        {error && (
          <div className="signup-qr-modal__error" role="alert">
            {error}
          </div>
        )}
      </Modal>
    );
  }
}

export default withTranslation()(SignupQrScanner);
